/**
 * @file Scalar helpers. Pure, allocation-free, safe to call per vertex.
 */

/**
 * @param {number} v
 * @param {number} a
 * @param {number} b
 * @returns {number}
 */
export const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);

/**
 * @param {number} v
 * @returns {number} `v` clamped to [0,1].
 */
export const saturate = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * @param {number} a
 * @param {number} b
 * @param {number} t
 * @returns {number}
 */
export const lerp = (a, b, t) => a + (b - a) * t;

/**
 * Inverse lerp: where does `v` sit between `a` and `b`? Unclamped, and 0 when
 * the range is degenerate.
 * @param {number} a
 * @param {number} b
 * @param {number} v
 * @returns {number}
 */
export const invLerp = (a, b, v) => (b === a ? 0 : (v - a) / (b - a));

/**
 * Hermite smoothstep between two edges. Works with `edge0 > edge1` for a
 * reversed ramp. Argument order matches GLSL/the sibling projects:
 * `smooth(edge0, edge1, x)`.
 * @param {number} edge0
 * @param {number} edge1
 * @param {number} x
 * @returns {number} 0..1
 */
export function smooth(edge0, edge1, x) {
  const t = saturate(invLerp(edge0, edge1, x));
  return t * t * (3 - 2 * t);
}

/**
 * Frame-rate independent exponential approach — the ONLY correct way to do
 * `current += (target - current) * k` when `dt` varies.
 * @param {number} current
 * @param {number} target
 * @param {number} lambda Higher = snappier (camera follow uses ~9).
 * @param {number} dt Seconds.
 * @returns {number}
 */
export const damp = (current, target, lambda, dt) =>
  lerp(current, target, 1 - Math.exp(-lambda * dt));

/**
 * Euclidean modulo — the result carries the sign of `n`, unlike JS `%`.
 * @param {number} v
 * @param {number} n
 * @returns {number}
 */
export const mod = (v, n) => ((v % n) + n) % n;

/**
 * Shortest signed angular difference `b - a`, wrapped to (-PI, PI].
 * @param {number} a Radians.
 * @param {number} b Radians.
 * @returns {number}
 */
export const angleDelta = (a, b) => mod(b - a + Math.PI, Math.PI * 2) - Math.PI;

/**
 * Interpolates angles the short way round.
 * @param {number} a Radians.
 * @param {number} b Radians.
 * @param {number} t
 * @returns {number}
 */
export const angleLerp = (a, b, t) => a + angleDelta(a, b) * t;

/**
 * Frame-rate independent angular approach.
 * @param {number} current Radians.
 * @param {number} target Radians.
 * @param {number} lambda
 * @param {number} dt
 * @returns {number}
 */
export const dampAngle = (current, target, lambda, dt) =>
  angleLerp(current, target, 1 - Math.exp(-lambda * dt));

/**
 * Maps `v` from one range to another, clamped to the output range.
 * @param {number} v
 * @param {number} inA
 * @param {number} inB
 * @param {number} outA
 * @param {number} outB
 * @returns {number}
 */
export const remap = (v, inA, inB, outA, outB) =>
  lerp(outA, outB, saturate(invLerp(inA, inB, v)));

/**
 * @param {number} ax
 * @param {number} az
 * @param {number} bx
 * @param {number} bz
 * @returns {number} Squared XZ distance (no sqrt — use for comparisons).
 */
export const dist2XZ = (ax, az, bx, bz) => {
  const dx = ax - bx;
  const dz = az - bz;
  return dx * dx + dz * dz;
};

export const DEG2RAD = Math.PI / 180;
export const RAD2DEG = 180 / Math.PI;
export const TAU = Math.PI * 2;
